// Can be used with single/multi select filter, chip list filter

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AgFloatingFilterComponent, CustomFloatingFilterParams } from '../ag-floating-filter.component';
import { CustomFilterParams } from '../ag-filter.component';
import { Observable, of } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { GxInputDirective } from '../../../../directives/gxinput.directive';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { NgClass, NgStyle } from '@angular/common';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { LetDirective } from '@ngrx/component';
import { ISimpleFilterModelType } from '../ag-text-floating-filter/ag-text-floating-filter.component';


export interface ListFloatingFilterParams extends CustomFloatingFilterParams {
  // do not show the name of the first active filter + amount of other active filters, just the
  // active filter amount
  showActiveFilterAmountOnly: boolean;
}

// can be combined with select, multi-select and chip filter
@Component({
    selector: 'app-list-floating-filter',
    templateUrl: './ag-list-floating-filter.component.html',
    styleUrls: ['./ag-list-floating-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LetDirective,
        MatFormField,
        NgClass,
        MatInput,
        FormsModule,
        GxInputDirective,
        ReactiveFormsModule,
        MatIcon,
        MatSuffix,
        MatIconButton,
        NgStyle,
        TranslateModule,
    ],
})
export class AgListFloatingFilterComponent extends AgFloatingFilterComponent implements OnInit {

  columnResizedListener = () => this.cd.markForCheck();

  constructor(private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // set timeout to give component time to render
    setTimeout(() => {
      // manual cd needed for correct el. size calculation in html template after component
      // initializes
      this.cd.markForCheck();
      // manual cd needed for correct el. size calculation in html template after column is resized
      this.params.api?.addEventListener('columnResized',
          this.columnResizedListener);
    });
    // remove event listener when component is destroyed
    this.componentDestroyed
        .pipe(take(1))
        .subscribe(() => {
          this.params.api
              .removeEventListener('columnResized', this.columnResizedListener)
        });
  }



  protected override setFormControlValueFromParentFilterModel(parentModel: string | string[]) {
    if ((this.params as ListFloatingFilterParams).showActiveFilterAmountOnly) {
      this.setFormControlValueProgrammatically(parentModel?.length?.toString(10));
      return;
    }

    // support for single select filter
    if (!Array.isArray(parentModel)) {
      parentModel = [parentModel];
    }

    let filterOptions = (this.params.filterParams as CustomFilterParams).filterOptions;

    // support for chip filter which should not take filter options param - use the first filter value instead
    if (!filterOptions) {
      filterOptions = [{name: parentModel[0], value: parentModel[0]}];
    }

    // filter options might be observable, so make sure to convert non-observable values
    if (!(filterOptions instanceof Observable)) {
      filterOptions = of(filterOptions);
    }

    // make sure to show the filter name, not the value
    filterOptions.pipe(take(1), takeUntil(this.componentDestroyed))
        .subscribe((options) => {
          let firstFilterValue: string = options.find((o) =>
              o.value === parentModel[0])?.name;

          const formattedFilterValue = parentModel.length > 1 ?
              `${firstFilterValue} (+${parentModel.length - 1})` :
              `${firstFilterValue}`;

          this.setFormControlValueProgrammatically(formattedFilterValue);
    })
  }

  protected override getDefaultType(): ISimpleFilterModelType {
    return null;
  }

  protected override getInitialReadOnly(): boolean {
    return true;
  }

  resetParentFilter() {
    this.params.parentFilterInstance((instance: any) => {
      instance.handleReset();
    });
  }
}
